import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * Pin the document to the viewport for a route that owns the whole screen.
 *
 * A `100dvh` container with `overflow-hidden` stops that CONTAINER scrolling — it
 * does nothing about the document underneath, and iOS Safari will happily let you
 * drag the page to collapse the URL bar. On the messages screen that took the app
 * header off the top and left the body background showing as a gap at the bottom.
 * Android's browser doesn't allow that drag, which is why it only showed on iOS.
 *
 * THE KEYBOARD IS THE CATCH. Once the document can't scroll, iOS can no longer
 * scroll a focused field into view — so a locked page would put the composer
 * behind the keyboard, which is worse than the scrolling it fixes. visualViewport
 * reports the area actually visible (it shrinks when the keyboard opens), so the
 * height comes from there and the composer stays just above the keyboard.
 * `dvh` can't do this: it tracks browser chrome, not the keyboard.
 *
 * The lock deliberately does NOT use `position: fixed` on body. That reads as the
 * stronger lock and behaves far worse: iOS still wants the focused field visible,
 * and with an immovable body it shifts the whole visual viewport instead, throwing
 * the app up off the top of the screen. Keeping body merely unscrollable leaves
 * iOS nothing it needs to shift. `sync` below also pulls the window back to 0 if
 * it ever does drift, which is the belt to that braces.
 *
 * On the native shell it also listens to Capacitor's keyboardWillShow, which
 * fires at the start of the keyboard animation and carries its height —
 * visualViewport only reports the change once the WebView has finished resizing,
 * which made the composer visibly trail the keyboard up the screen.
 *
 * Applied as a class on <html> for the lifetime of the route, so every other page
 * keeps scrolling normally. See `.app-locked` / `.app-viewport` in index.css.
 *
 * Nest-safe — a counter means an inner locked route unmounting doesn't unlock the
 * outer one that's still mounted.
 */
let lockCount = 0;

export default function useViewportLock(active = true) {
  useEffect(() => {
    if (!active) return undefined;

    const root = document.documentElement;
    lockCount += 1;
    root.classList.add('app-locked');

    // Track the visible area. Falls back to the CSS default (100%) where
    // visualViewport isn't available — older browsers then behave as before.
    const vv = window.visualViewport;
    const sync = () => {
      if (!vv) return;
      root.style.setProperty('--app-vh', `${Math.round(vv.height)}px`);

      // If iOS shifted the page anyway (it does this to reveal a focused field),
      // put it back — the container is already sized to what's visible, so there
      // is nothing legitimate to scroll to.
      if (window.scrollY !== 0) window.scrollTo(0, 0);
    };
    sync();
    vv?.addEventListener('resize', sync);
    // The keyboard can also offset the visual viewport rather than resize it.
    vv?.addEventListener('scroll', sync);

    // NATIVE: visualViewport only reports the keyboard once the WebView has
    // actually been resized, which is the END of the show animation — so the
    // composer arrived late and appeared to lag the keyboard up the screen.
    // Capacitor's keyboardWillShow fires at the START and carries the height, so
    // the layout can move with the keyboard instead of after it. The
    // visualViewport listener above still runs and settles on the same value.
    let detachKeyboard = null;
    if (Capacitor.isNativePlatform()) {
      (async () => {
        try {
          const { Keyboard } = await import('@capacitor/keyboard');
          const show = await Keyboard.addListener('keyboardWillShow', (info) => {
            const h = window.innerHeight - (info?.keyboardHeight || 0);
            if (h > 0) root.style.setProperty('--app-vh', `${Math.round(h)}px`);
          });
          const hide = await Keyboard.addListener('keyboardWillHide', () => {
            root.style.setProperty('--app-vh', `${Math.round(vv?.height || window.innerHeight)}px`);
          });
          detachKeyboard = () => { show.remove(); hide.remove(); };
        } catch {
          // Plugin missing (web build, or a shell without it) — visualViewport
          // alone still works, just without the head start.
        }
      })();
    }

    return () => {
      vv?.removeEventListener('resize', sync);
      vv?.removeEventListener('scroll', sync);
      detachKeyboard?.();
      lockCount -= 1;
      if (lockCount <= 0) {
        lockCount = 0;
        root.classList.remove('app-locked');
        root.style.removeProperty('--app-vh');
      }
    };
  }, [active]);
}
